import React, { useState } from 'react';
import axios from 'axios';
import { useVideo } from '../../../contexts/VideoContext';
import './Steps.css';

const BREEDS = [
  'Golden Retriever',
  'Labrador Retriever',
  'German Shepherd',
  'French Bulldog',
  'Bulldog',
  'Poodle',
  'Beagle',
  'Rottweiler',
  'Yorkshire Terrier',
  'Boxer',
  'Dachshund',
  'Siberian Husky',
  'Corgi',
  'Shih Tzu',
  'Chihuahua',
  'Pug',
  'Australian Shepherd',
  'Border Collie',
  'Cocker Spaniel',
  'Mixed Breed',
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

const Step2Photo = () => {
  const { videoData, updateVideoData, nextStep, prevStep } = useVideo();
  const [uploadMode, setUploadMode] = useState(videoData.useBreedLibrary ? 'breed' : 'upload');
  const [selectedBreed, setSelectedBreed] = useState(videoData.selectedBreed || '');
  const [photoPreview, setPhotoPreview] = useState(videoData.photoUrl || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be smaller than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    // Create preview immediately
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    try {
      // Create FormData and upload directly to backend
      const formData = new FormData();
      formData.append('photo', file);

      const response = await axios.post(
        `${API_URL}/api/customer/photos/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const { fileUrl } = response.data;

      // Save the S3 URL
      updateVideoData({ photoUrl: fileUrl });
      setUploading(false);
      console.log('✅ Photo uploaded to S3:', fileUrl);
    } catch (err) {
      console.error('Photo upload error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to upload photo. Please try again.';
      setError(errorMessage);
      setUploading(false);
    }
  };

  const handleNext = () => {
    if (uploading) {
      setError('Please wait for photo upload to complete');
      return;
    }

    if (uploadMode === 'upload' && !videoData.photoUrl && !photoPreview) {
      setError('Please upload a photo of your pet');
      return;
    }

    if (uploadMode === 'breed' && !selectedBreed) {
      setError('Please select a breed');
      return;
    }

    updateVideoData({
      useBreedLibrary: uploadMode === 'breed',
      selectedBreed: uploadMode === 'breed' ? selectedBreed : '',
      // photoUrl is already set during upload
    });

    nextStep();
  };

  return (
    <div className="wizard-step">
      <div className="step-content">
        <div className="step-icon">📸</div>
        <h2 className="step-title">Show Us Your Pet!</h2>
        <p className="step-description">
          Upload a photo of {videoData.petName} or choose a breed
        </p>

        {/* Mode Toggle */}
        <div className="mode-toggle">
          <button
            type="button"
            className={`mode-btn ${uploadMode === 'upload' ? 'active' : ''}`}
            onClick={() => {
              setUploadMode('upload');
              setError('');
            }}
          >
            📤 Upload Photo
          </button>
          <button
            type="button"
            className={`mode-btn ${uploadMode === 'breed' ? 'active' : ''}`}
            onClick={() => {
              setUploadMode('breed');
              setError('');
            }}
          >
            🐕 Choose Breed
          </button>
        </div>

        {/* Upload Mode */}
        {uploadMode === 'upload' && (
          <div className="upload-section">
            <label className="upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              {uploading ? (
                <div className="upload-placeholder">
                  <span className="spinner"></span>
                  <p>Uploading...</p>
                </div>
              ) : photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Pet preview"
                  className="photo-preview"
                />
              ) : (
                <div className="upload-placeholder">
                  <span className="upload-icon">📷</span>
                  <p>Click to upload a photo</p>
                  <span className="upload-hint">Max size: 5MB</span>
                </div>
              )}
            </label>
            {photoPreview && !uploading && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setPhotoPreview('');
                  updateVideoData({ photoUrl: '' });
                }}
              >
                Change Photo
              </button>
            )}
          </div>
        )}

        {/* Breed Selection Mode */}
        {uploadMode === 'breed' && (
          <div className="breed-section">
            <select
              value={selectedBreed}
              onChange={(e) => {
                setSelectedBreed(e.target.value);
                setError('');
              }}
              className="form-select"
            >
              <option value="">Select a breed...</option>
              {BREEDS.map((breed) => (
                <option key={breed} value={breed}>
                  {breed}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {/* Navigation */}
        <div className="step-navigation">
          <button type="button" className="btn-secondary" onClick={prevStep}>
            <span className="btn-icon">←</span>
            Back
          </button>
          <button type="button" className="btn-primary" onClick={handleNext}>
            Next Step
            <span className="btn-icon">→</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2Photo;
